/* eslint-disable no-underscore-dangle */
import * as _ from 'lodash';
import { nanoid } from 'nanoid';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import { dropAt } from '../../../../utils';
import Button from '../../../generic/Button/Button';
import NavigationButton from '../../../generic/Button/NavigationButton';
import LabeledCheckbox from '../../../generic/Forms/LabeledCheckbox';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import { icons, IconText } from '../../../generic/Icon';
import { DOCS_URL } from '../../../generic/Link/DocsLink';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import useLanguageSettings from '../useLanguageSettings';
import type { BowerFetcherConfig, BowerRegistry } from '../../../../../typings/auth';

import Registry from './Registry';

const JAVASCRIPT_DOCS_LINK = `${DOCS_URL}/javascript`;

const artifactoryErrorMessage = 'If you are using Artifactory, you must provide one registry URL.';

const BowerSettings: VoidFunctionComponent = () => {
  const {
    settings,
    isSaving,
    onUpdateSettings,
    onSaveSettings,
    isSaveButtonDisabled,
    isLoading,
  } = useLanguageSettings(
    window.user?.organizationId as number,
    'bower',
  );

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const registries = settings?.registries || [];
  const useArtifactory = !!settings?.useArtifactory;

  // Call before onUpdateSettings to toggle the artifactory alert
  const updateSettings = (updatedSettings: Partial<BowerFetcherConfig>) => {
    onUpdateSettings(updatedSettings);

    // These checks ensure that if useArtifactory is true, then the amount of registries
    // MUST be 1
    const willUseArtifactory = !_.isNil(updatedSettings.useArtifactory)
      ? updatedSettings.useArtifactory
      : useArtifactory;

    const willRegistriesBeOfLengthOne = (!_.isNil(updatedSettings.registries)
      ? updatedSettings.registries
      : registries
    ).length !== 1;

    if (willUseArtifactory && willRegistriesBeOfLengthOne) {
      // If using artifactory, expect exactly one URL.
      setAlertMessage(artifactoryErrorMessage);
    } else {
      setAlertMessage(null);
    }
  };

  const onAddRegistry = () =>
    updateSettings({
      registries: [
        ...registries,
        {
          url: { __type: 's', value: '' },
          _id: nanoid(),
        },
      ],
    });

  const onRemoveRegistry = (index: number) =>
    updateSettings({
      registries: dropAt(registries, index),
    });

  const onUpdatePassword = (index: number, value: string) =>
    updateSettings({
      registries: registries.map((registry, i) => {
        if (i !== index) {
          return registry;
        }

        const updatedRegistry: BowerRegistry = {
          ...registry,
          url: { __type: 's', value },
          hasUrl: false,
        };

        return updatedRegistry;
      }),
    });

  const onUpdateUseArtifactory = (checked: boolean) => {
    let newRegistries: BowerRegistry[] | undefined;
    if (checked && !registries.length) {
      newRegistries = [{ url: { __type: 's', value: '' }, _id: nanoid() }];
    }

    updateSettings({
      registries: newRegistries || registries,
      useArtifactory: checked,
    });
  };

  const isFreeOrg = isOrgInFreeTier();

  return (
    <PaidFeatureGate isRestricted={isFreeOrg} hidePill>
      <Header heading="Bower Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled || !!alertMessage}
          isLoading={isSaving}
        />
        {alertMessage && <Header.Warning message={alertMessage} />}
      </Header>
      {isLoading
        ? <Loader />
        : (
          <Section>
            <Section.Left heading="Private Registries">
              <Section.Paragraph>
                Enter the URL of each of your private registries.
              </Section.Paragraph>
              <Section.Paragraph>
                The URLs may include authentication information in the form of <code>user:password</code>.
                {' '}
                This configuration is stored in encrypted form.
              </Section.Paragraph>
              <NavigationButton
                type="secondary"
                size="medium"
                to={JAVASCRIPT_DOCS_LINK}
                openInNewTab
              >
                View documentation
              </NavigationButton>
            </Section.Left>
            <Section.Right>
              <div className="margin-bottom-sm">
                <LabeledCheckbox
                  label="Use Artifactory"
                  isChecked={useArtifactory}
                  onChange={(_evt: unknown, checked: boolean) => onUpdateUseArtifactory(checked)}
                  tooltip="If you are using Artifactory, check this box and specify only one registry."
                />
              </div>
              {registries.map((registry, index) => (
                <Registry
                  key={registry._id}
                  registry={registry}
                  onUpdateRegistryURL={(value: string) => {
                    onUpdatePassword(index, value);
                  }}
                  onRemoveRegistry={() => onRemoveRegistry(index)}
                  isRemoveDisabled={registries.length === 1 && useArtifactory}
                />
              ))}
              {(!useArtifactory || !registries.length) && (
                <Button
                  type="secondary"
                  size="medium"
                  onClick={onAddRegistry}
                >
                  <IconText icon={icons.plus} text="Add registry" />
                </Button>
              )}
            </Section.Right>
          </Section>
          )}
    </PaidFeatureGate>
  );
};

export default BowerSettings;
